import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { motion } from "motion/react";

import { z } from "zod/v4";
import { PackageCard } from "@/components/packages/package-card";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFilteredPackages, usePackageFilterOptions } from "@/hooks/use-packages";

import type { PackageFilters, PackageListItem } from "@/lib/types";

const PAGE_SIZE = 20;

/** Value used to represent "All" / unset in Select (Radix doesn't allow empty string) */
const ALL = "__all__";

// Helper: parse comma-separated string or array from URL params
const csvArray = z.preprocess((val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === "string") return val.split(",").filter(Boolean);
  return undefined;
}, z.array(z.string()).optional());

const searchSchema = z.object({
  season: z.string().optional(),
  week: z.coerce.number().optional(),
  series: csvArray,
  page: z.coerce.number().optional(),
});

function groupByWeek(packages: PackageListItem[]): [number, PackageListItem[]][] {
  const groups = new Map<number, PackageListItem[]>();
  for (const pkg of packages) {
    const week = pkg.week_number;
    const group = groups.get(week);
    if (group) {
      group.push(pkg);
    } else {
      groups.set(week, [pkg]);
    }
  }
  return [...groups.entries()].sort((a, b) => b[0] - a[0]);
}

const GRID_CLASSES = "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3";

export const Route = createFileRoute("/_auth/app/packages/")({
  validateSearch: searchSchema,
  component: PackagesBrowsePage,
});

function PackagesBrowsePage() {
  const navigate = useNavigate();
  const search = Route.useSearch();

  // Build filters from search params — no default season/week, show all packages on first visit
  const filters: PackageFilters = {
    season: search.season,
    week: search.week,
    series: search.series,
    page: search.page,
  };

  const { data, isLoading, isError } = useFilteredPackages(filters);
  const { data: filterOptions } = usePackageFilterOptions(filters);

  function handleFilterChange(newFilters: PackageFilters) {
    // Strip undefined/empty values; serialize arrays as comma-separated
    const cleaned: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(newFilters)) {
      if (v == null) continue;
      if (Array.isArray(v)) {
        if (v.length > 0) cleaned[k] = v.join(",");
      } else {
        cleaned[k] = v;
      }
    }
    navigate({
      to: "/app/packages",
      search: cleaned as any,
    });
  }

  function update(patch: Partial<PackageFilters>) {
    handleFilterChange({ ...filters, ...patch, page: 1 });
  }

  /** Convert Select value back — ALL sentinel -> undefined */
  function val(v: string): string | undefined {
    return v === ALL ? undefined : v;
  }

  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const hasActiveFilters = (filters.series?.length ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="font-[Bebas_Neue] text-3xl text-foreground">Packages</h1>
        <p className="text-sm text-muted-foreground">
          Download curated setup bundles ready for iRacing
        </p>
      </div>

      {/* Filters — loaded independently so they don't block packages */}
      {filterOptions && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Season */}
          <Select
            value={filters.season ?? ALL}
            onValueChange={(v) => update({ season: val(v), week: undefined })}
          >
            <SelectTrigger className="h-8 w-[150px] border-input bg-background text-xs hover:bg-accent hover:text-accent-foreground">
              <SelectValue placeholder="All Seasons" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Seasons</SelectItem>
              {filterOptions.seasons.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Week */}
          <Select
            value={filters.week != null ? String(filters.week) : ALL}
            onValueChange={(v) => update({ week: v !== ALL ? Number(v) : undefined })}
          >
            <SelectTrigger className="h-8 w-[150px] border-input bg-background text-xs hover:bg-accent hover:text-accent-foreground">
              <SelectValue placeholder="All Weeks" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Weeks</SelectItem>
              {filterOptions.weeks.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Series — searchable multi-select */}
          <Combobox
            placeholder="All Series"
            searchPlaceholder="Search series..."
            options={filterOptions.series}
            value={filters.series ?? []}
            onValueChange={(v) => update({ series: v.length ? v : undefined })}
            className="w-[180px]"
          />

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                handleFilterChange({
                  season: filters.season,
                  week: filters.week,
                  page: 1,
                })
              }
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear all
            </Button>
          )}
        </div>
      )}

      {/* Results count */}
      {!isLoading && !isError && (
        <p className="text-sm text-muted-foreground">
          {total} {total === 1 ? "package" : "packages"} found
        </p>
      )}

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl border border-border/50 bg-card">
              <div className="h-24 rounded-t-xl bg-muted" />
              <div className="space-y-2 p-4">
                <div className="h-5 w-3/4 rounded bg-muted" />
                <div className="h-4 w-full rounded bg-muted" />
                <div className="h-3 w-1/2 rounded bg-muted" />
                <div className="h-3 w-1/3 rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {!isLoading && isError && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-destructive/30 bg-destructive/5 py-16">
          <p className="text-destructive">Failed to load packages. Please try again.</p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-3 text-xs"
            onClick={() => window.location.reload()}
          >
            Reload page
          </Button>
        </div>
      )}

      {/* Results grouped by week */}
      {!isLoading && !isError && data && data.packages.length > 0 && (
        <div className="space-y-6">
          {groupByWeek(data.packages).map(([week, pkgs], groupIndex) => (
            <motion.div
              key={week}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: groupIndex * 0.08 }}
            >
              <div className="mb-3 flex items-center gap-3">
                <h2 className="font-[Bebas_Neue] text-3xl text-blue-500">Week {week}</h2>
                <div
                  className="relative h-14 flex-1"
                  style={{
                    backgroundImage: "url(/kalocsai-strip-tile.webp)",
                    backgroundRepeat: "repeat-x",
                    backgroundSize: "400px 140%",
                    backgroundPosition: "center",
                    opacity: 0.12,
                    maskImage:
                      "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent), linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                    WebkitMaskImage:
                      "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent), linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                    maskComposite: "intersect",
                    WebkitMaskComposite: "destination-in",
                  }}
                />
                <span className="text-xs text-muted-foreground">
                  {pkgs.length} {pkgs.length === 1 ? "package" : "packages"}
                </span>
              </div>
              <div className={GRID_CLASSES}>
                {pkgs.map((pkg: PackageListItem) => (
                  <PackageCard key={pkg.id} pkg={pkg} />
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && data && data.packages.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border/50 py-16">
          <p className="text-muted-foreground">No packages available for this week.</p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-3 text-xs"
            onClick={() =>
              handleFilterChange({
                season: filters.season,
                week: filters.week,
                page: 1,
              })
            }
          >
            Clear filters
          </Button>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => handleFilterChange({ ...filters, page: page - 1 })}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => handleFilterChange({ ...filters, page: page + 1 })}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
